/**
 * Publisher embed layer.
 *
 * Everything the CMS snippet used to do by hand — article history tracking,
 * the `window.MillaInteractions` helpers, OneSignal identity sync, ad
 * placeholder toggles and the brand ads helper globals — lives here so the
 * CMS only loads the SDK and calls `MillaAuth.init({ issuer, embed: true })`.
 *
 * Owner decision 2026-08-26 (engagement plan B2): history is recorded only on
 * article pages, once per article id per page load, and only for an
 * authenticated member. The once-per-page guard uses the same
 * `window.__millaHistoryTracked` global as the legacy CMS snippet so both can
 * coexist during the CMS transition without double-counting.
 */

const identity = require('./lib/identity');

const KNOWN_BRANDS = ['narcity', 'mtlblog'];

const noop = () => {};

function log(level, message, data) {
  if (typeof console === 'undefined') return;
  const fn = console[level] || console.log;
  try {
    if (data === undefined) fn(`[milla-embed] ${message}`);
    else fn(`[milla-embed] ${message}`, data);
  } catch {
    // ignore console failures
  }
}

function normalizeEmbedOptions(embed, config = {}) {
  if (!embed) return null;
  const base = {
    brand: null,
    articleTracking: true,
    // Unified Visitor Identity (Flow B): anonymous `article_viewed` capture
    // under the visitor credential, once per article id per page load.
    identityCapture: true,
    interactions: true,
    oneSignal: true,
    adHelpers: true,
    preloadAds: null,
    debug: false
  };
  const options = typeof embed === 'object' ? { ...base, ...embed } : { ...base };
  if (typeof embed === 'string') options.brand = embed;
  options.brand = resolveBrand(options.brand, config.issuer);
  return options;
}

function resolveBrand(brand, issuer) {
  if (typeof brand === 'string' && KNOWN_BRANDS.includes(brand.toLowerCase())) {
    return brand.toLowerCase();
  }
  const host = hostOf(issuer) || (typeof window !== 'undefined' && window.location ? window.location.hostname : '');
  return /mtlblog/i.test(host || '') ? 'mtlblog' : 'narcity';
}

function hostOf(url) {
  if (typeof url !== 'string' || !url) return '';
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

function pageHost() {
  if (typeof window === 'undefined' || !window.location) return '';
  return String(window.location.hostname || '').replace(/^www\./, '');
}

function metaContent(selector) {
  if (typeof document === 'undefined' || typeof document.querySelector !== 'function') return null;
  const el = document.querySelector(selector);
  return el && typeof el.getAttribute === 'function' ? el.getAttribute('content') : null;
}

/**
 * True only on article pages. Homepages, tag/author/section pages and the
 * portal never record reading history (plan taxonomy: ARTICLE_VIEWED).
 */
function isArticlePage() {
  if (typeof window === 'undefined') return false;
  if (typeof window.articleId === 'string' && window.articleId) return true;
  if (typeof window.rm_article_id === 'string' && window.rm_article_id) return true;
  if (metaContent('meta[name="narcity:article_id"]')) return true;
  const ogType = metaContent('meta[property="og:type"]');
  return typeof ogType === 'string' && ogType.toLowerCase() === 'article';
}

/**
 * Article payload for history/favourites. Shared with the auth bar so the
 * save control and the history trigger describe the same article.
 */
function buildArticlePayload() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null;
  try {
    const id =
      (typeof window.articleId === 'string' && window.articleId) ||
      (typeof window.rm_article_id === 'string' && window.rm_article_id) ||
      metaContent('meta[name="narcity:article_id"]') ||
      (window.location && window.location.pathname);

    const title = metaContent('meta[property="og:title"]') || document.title || `Article ${id}`;
    const payload = {
      id,
      title,
      url: window.location ? window.location.href : undefined,
      source: pageHost() || 'narcity.com'
    };
    const image = metaContent('meta[property="og:image"]');
    if (image) payload.imageUrl = image;
    if (window.articleSection) payload.section = window.articleSection;
    if (window.articleAuthor) payload.author = window.articleAuthor;
    return payload;
  } catch {
    return null;
  }
}

function trackedMap() {
  if (typeof window === 'undefined') return {};
  window.__millaHistoryTracked = window.__millaHistoryTracked || {};
  return window.__millaHistoryTracked;
}

/**
 * Records one ARTICLE_VIEWED for the current article, once per page load,
 * only for an authenticated member on an article page. Returns the reason it
 * skipped, or 'tracked'.
 */
function trackArticleView(api, source, options = {}) {
  if (!isArticlePage()) return 'not-article-page';
  const article = buildArticlePayload();
  const id = article && article.id;
  if (!id) return 'no-article-id';
  if (typeof api.isAuthenticated === 'function' && !api.isAuthenticated()) return 'not-authenticated';
  if (typeof api.addToHistory !== 'function') return 'unavailable';
  const tracked = trackedMap();
  if (tracked[id]) return 'already-tracked';
  tracked[id] = true;
  if (options.debug) log('log', 'trackArticleView', { source, articleId: id });
  try {
    Promise.resolve(api.addToHistory(article)).catch((err) => {
      // Let a later auth event retry (token refresh, login after load).
      delete tracked[id];
      log('warn', 'addToHistory rejected', err && err.code ? err.code : err);
    });
  } catch (err) {
    delete tracked[id];
    log('warn', 'addToHistory threw', err);
  }
  return 'tracked';
}

function setupArticleTracking(api, options) {
  const track = (source) => trackArticleView(api, source, options);
  // Already authenticated (session restored before init finished).
  track('init');
  if (typeof api.on !== 'function') return;
  api.on('auth:ready', (state) => {
    if (state && state.authenticated) track('auth:ready');
  });
  // Covers login after page load and token refreshes; the guard keeps it to one write.
  api.on('token', () => track('token'));
}

function installInteractions(api, options) {
  if (typeof window === 'undefined') return null;
  const interactions = {
    buildArticlePayload,
    isArticlePage,
    trackView: () => trackArticleView(api, 'manual', options),
    addFavourite: (article) => {
      if (typeof api.addFavourite !== 'function') return Promise.resolve(null);
      return api.addFavourite(article || buildArticlePayload());
    },
    removeFavourite: (articleId) => {
      if (typeof api.removeFavourite !== 'function') return Promise.resolve(null);
      return api.removeFavourite(articleId);
    },
    isFavourite: (articleId) => {
      if (typeof api.isFavourite !== 'function') return Promise.resolve({ isSaved: false });
      return api.isFavourite(articleId);
    },
    toggleFavourite(article, isCurrentlySaved) {
      const payload = article || buildArticlePayload();
      const id = payload && payload.id;
      if (!id) return Promise.resolve(null);
      return isCurrentlySaved ? this.removeFavourite(id) : this.addFavourite(payload);
    },
    summarize: (text) => {
      if (typeof api.summarize !== 'function') return Promise.resolve(null);
      return api.summarize(text);
    }
  };
  window.MillaInteractions = interactions;
  return interactions;
}

function setupOneSignal(api, config) {
  if (typeof window === 'undefined') return;
  const issuer = String(config.issuer || '').replace(/\/$/, '');

  const sync = () => {
    if (!window.OneSignal || typeof api.fetch !== 'function') return;
    api
      .fetch(`${issuer}/me`, { credentials: 'include' })
      .then((res) => (res && res.ok ? res.json().catch(() => null) : null))
      .then((profile) => {
        if (!profile) return;
        const memberId = profile.id || profile.userId || profile.authKitUserId;
        if (typeof memberId === 'string' && typeof window.OneSignal.login === 'function') {
          window.OneSignal.login(memberId);
        }
      })
      .catch((err) => log('warn', 'OneSignal sync failed', err));
  };

  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(sync);
  if (typeof api.on === 'function') {
    api.on('token', sync);
    api.on('logout', () => {
      if (window.OneSignal && typeof window.OneSignal.logout === 'function') window.OneSignal.logout();
    });
  }
}

const AD_HELPER_PAGE_TYPES = ['article', 'homepage'];
const AD_HELPER_DEVICES = [null, 'desktop', 'mobile', 'tablet'];

function installAdHelpers(api, options) {
  if (typeof window === 'undefined') return null;
  window.enableMillaAdPlaceholders = () => { window._millaShowAdPlaceholders = true; };
  window.disableMillaAdPlaceholders = () => { window._millaShowAdPlaceholders = false; };
  if (window._millaShowAdPlaceholders === undefined) window._millaShowAdPlaceholders = false;

  const initName = options.brand === 'mtlblog' ? 'initMtlblogAds' : 'initNarcityAds';
  const helper = {};
  for (const pageType of AD_HELPER_PAGE_TYPES) {
    for (const device of AD_HELPER_DEVICES) {
      const name = `init${pageType[0].toUpperCase()}${pageType.slice(1)}${device ? device[0].toUpperCase() + device.slice(1) : ''}`;
      helper[name] = () => {
        if (typeof api[initName] !== 'function') return null;
        const adsConfig = { pageType, ignoreProStatus: false };
        if (device) adsConfig.device = device;
        return api[initName](adsConfig);
      };
    }
  }
  const globalName = options.brand === 'mtlblog' ? 'MtlblogAds' : 'NarcityAds';
  window[globalName] = helper;
  return helper;
}

function preloadAds(api, options) {
  if (!options.preloadAds || window.__millaAdsPreloaded) return;
  const preloadName = options.brand === 'mtlblog' ? 'preloadMtlblogAds' : 'preloadNarcityAds';
  if (typeof api[preloadName] !== 'function') return;
  window.__millaAdsPreloaded = true;
  const preloadConfig = typeof options.preloadAds === 'object' ? options.preloadAds : { pageType: isArticlePage() ? 'article' : 'homepage' };
  try {
    api[preloadName](preloadConfig);
  } catch (err) {
    log('warn', 'ad preload failed', err);
  }
}

// init() calls this before the auth bar, comments and app prompt so the ad
// scripts start downloading first; setup() then skips its own preload.
function preloadAdScripts(api, config) {
  if (typeof window === 'undefined') return;
  const options = normalizeEmbedOptions(config && config.embed, config);
  if (options) preloadAds(api, options);
}

/**
 * Installs the embed layer once per page. Returns the resolved options.
 */
function setup(api, config) {
  const options = normalizeEmbedOptions(config && config.embed, config);
  if (!options || typeof window === 'undefined') return null;
  if (window.__millaEmbedInstalled) return window.__millaEmbedInstalled;
  window.__millaEmbedInstalled = options;
  if (options.preloadAds) preloadAds(api, options);
  if (options.interactions) installInteractions(api, options);
  if (options.articleTracking) setupArticleTracking(api, options);
  if (options.identityCapture) {
    try {
      const disposition = identity.captureArticleView();
      if (options.debug) log('log', 'identity article capture', { disposition });
    } catch (err) {
      log('warn', 'identity article capture failed', err);
    }
  }
  if (options.oneSignal) setupOneSignal(api, config);
  if (options.adHelpers) installAdHelpers(api, options);
  return options;
}

module.exports = {
  setup,
  preloadAdScripts,
  normalizeEmbedOptions,
  resolveBrand,
  isArticlePage,
  buildArticlePayload,
  trackArticleView,
  installInteractions,
  installAdHelpers,
  setupOneSignal,
  noop
};
